import { NUMBERED_REF_TOKEN_RE } from '../citationTokens';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SiteRagReport } from '../../types/siteRag';

/** Trusted live generation frame, supplied independently of generated prose. */
export interface FrozenBomSubtotalSource {
  siteId: string;
  sed: Pick<SiteEvidenceDocument, 'id' | 'version' | 'supplyChainSnapshot'>;
  report: Pick<SiteRagReport, 'siteId' | 'sedId' | 'sedVersionUsed' | 'references'>;
}

const BOM = String.raw`(?:BoM|bill\s+of\s+materials)`;
const COST = String.raw`(?:CAPEX(?:\s+(?:total|subtotal))?|cost(?:\s+(?:total|subtotal))?|(?:priced\s+)?subtotal)`;
const BEFORE_ROLE = new RegExp(String.raw`\b${BOM}\s+${COST}\s*(?:(?:is|of|at|records?)\s+|[:=]\s*)?$`, 'i');
const AFTER_ROLE = new RegExp(String.raw`^\s+${BOM}\s+${COST}\b`, 'i');
const COMMITMENT = String.raw`(?:quoted|contracted|awarded|paid|committed)`;
const CONTRACT_COST = String.raw`(?:(?:EPC\s+)?(?:contract|quote|quotation|award)\s+(?:price|cost|amount|sum|value|budget)|${COMMITMENT}\s+(?:(?:EPC|contract|capital)\s+)?(?:price|cost|amount|sum|value|CAPEX|budget))`;
const PHASE_COST = String.raw`phase\s+(?:#\s*)?(?:[1-9]\d*|[ivx]+|one|two|three|four|five)\b(?:\s+(?:priced|recorded))?`;
const COST_LINK = String.raw`(?:(?:is|was|equals?|totals?|as|of|at|for|represents?|allocated\s+to|attributable\s+to|amounts?\s+to)\s+|[:=]\s*)?(?:(?:a|an|the)\s+)?`;
const SCOPED_PREFIX = new RegExp(String.raw`\b(?:${CONTRACT_COST}|${COMMITMENT}|(?:EPC\s+)?contract|${PHASE_COST})\s*${COST_LINK}$`, 'i');
const SCOPED_SUFFIX = new RegExp(String.raw`^\s*(?:[,:(]\s*)?${COST_LINK}(?:${CONTRACT_COST}|${PHASE_COST})\b`, 'i');
const COMMITTED_SUFFIX = new RegExp(String.raw`^\s*(?:(?:is|was|has\s+been|will\s+be)\s+)?(?:fully\s+)?${COMMITMENT}\b`, 'i');
const QUALIFIED_BEFORE_ROLE = new RegExp(String.raw`\b${BOM}\s+${COST}\s+${COST_LINK}(?:${COMMITMENT}|${CONTRACT_COST}|${PHASE_COST})\s*${COST_LINK}$`, 'i');
const QUALIFIED_AFTER_ROLE = new RegExp(String.raw`^\s*(?:${COMMITMENT}|${PHASE_COST})\s+${BOM}\s+${COST}\b`, 'i');

/** Inspect only grammar attached to this amount/role. A quoted transformer
 * mentioned later in a logistics sentence does not turn the whole plan's
 * recorded subtotal into a quote. A phase or contractual price does. */
function hasDifferentCostScope(lead: string, tail: string): boolean {
  const blankRefs = (text: string) => text.replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'), token => ' '.repeat(token.length));
  const before = blankRefs(lead);
  const after = blankRefs(tail);
  const roleBefore = BEFORE_ROLE.exec(before);
  const roleAfter = AFTER_ROLE.exec(after);
  const qualifiedRole = QUALIFIED_BEFORE_ROLE.test(before) || QUALIFIED_AFTER_ROLE.test(after);
  if (!roleBefore && !roleAfter && !qualifiedRole) return false;
  return qualifiedRole
    || SCOPED_PREFIX.test(before) || SCOPED_SUFFIX.test(after) || COMMITTED_SUFFIX.test(after)
    || (roleBefore != null && SCOPED_PREFIX.test(before.slice(0, roleBefore.index)))
    || (roleAfter != null && (SCOPED_SUFFIX.test(after.slice(roleAfter[0].length))
      || COMMITTED_SUFFIX.test(after.slice(roleAfter[0].length))));
}

/** Bind one explicit BOM subtotal token, never a project budget or a rate.
 * Ref N is resolved against THIS candidate's current array, before or after
 * ordinary source-preserving renumbering. Diagnostic historical refs cannot bind. */
export function checkSourceBoundBomSubtotal(args: {
  text: string; index: number; raw: string; value: number; unit: string;
  siteId: string; source?: FrozenBomSubtotalSource;
}): { supported: boolean; expected?: number } | undefined {
  if (args.unit !== 'USD') return undefined;
  const before = args.text.slice(Math.max(0, args.index - 400), args.index);
  const after = args.text.slice(args.index + args.raw.length, args.index + args.raw.length + 400);
  const lead = before.split(/[;\n]|\.(?=\s|$)|\b(?:but|whereas|however)\b/i).pop() ?? '';
  const tail = after.split(/[;\n]|\.(?=\s|$)|\b(?:but|whereas|however)\b/i)[0] ?? '';
  if (hasDifferentCostScope(lead, tail)) return { supported: false };
  if (!BEFORE_ROLE.test(lead) && !AFTER_ROLE.test(tail)) return undefined;
  const clause = `${lead}${args.raw}${tail}`;
  // A subtotal cannot establish all-in capital, contingency, or another
  // financial quantity just because it shares a value and a citation.
  if (/\ball[- ]in\b|\b(?:total|entire)\s+(?:project|campus|facility)\b|\b(?:project|campus|facility)\s+(?:CAPEX|budget|capital\s+cost)\b|\b(?:contingency|NPV|OPEX|revenue)\b/i.test(clause)) {
    return { supported: false };
  }
  const source = args.source;
  const snapshot = source?.sed.supplyChainSnapshot;
  const value = snapshot?.data?.bomCapexTotalUsd;
  if (!source || !source.siteId || source.siteId !== args.siteId
    || !source.sed.id || !Number.isInteger(source.sed.version) || source.sed.version <= 0
    || source.report.sedId !== source.sed.id
    // The outer typed coordinator stamps these after the internal gate.
    // A present stamp must agree; absence does not replace the trusted frame.
    || (source.report.siteId !== undefined && source.report.siteId !== source.siteId)
    || (source.report.sedVersionUsed !== undefined && source.report.sedVersionUsed !== source.sed.version)
    || snapshot?.status !== 'verified' || snapshot.source !== 'supply-chain-plan'
    || (snapshot.sourceGrade !== undefined && snapshot.sourceGrade !== 'regional-screening')
    || (snapshot.qualification !== undefined && snapshot.qualification !== 'screening')
    || !Number.isFinite(Date.parse(snapshot.retrievedAt))
    || !Number.isInteger(snapshot.data?.bomItemCount) || snapshot.data!.bomItemCount <= 0
    || typeof value !== 'number' || !Number.isFinite(value) || value < 0) return { supported: false };
  const tokens = [...clause.matchAll(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'))];
  const ids = tokens.flatMap(token => [...token[0].matchAll(/\d+/g)].map(match => Number(match[0])));
  if (ids.length !== 1 || tokens.some(token => /\b(?:unverified|inferred)\b/i.test(token[0]))) return { supported: false };
  const ref = source.report.references?.[ids[0]! - 1];
  if (ref?.field !== 'supplyChainSnapshot' || ref.source !== snapshot.source
    || ref.retrievedAt !== snapshot.retrievedAt || ref.url !== snapshot.sourceUrl
    || ref.qualification !== 'screening' || ref.sourceGrade !== 'regional-screening') return { supported: false };
  return { supported: args.value === value, expected: value };
}

/** Independent coordinator authority for one frozen SED's producer context. */
export interface BomSubtotalPromptSource {
  siteId?: string;
  sourceAuthority?: { ownerUid: string; siteId: string; sedId: string; sedVersion: number };
  sed: FrozenBomSubtotalSource['sed'];
  references: NonNullable<FrozenBomSubtotalSource['report']['references']>;
}

/** Give a procurement/cost producer the separately cited subtotal the unchanged final
 * grounder can admit. A live plan or a financial document alone cannot supply
 * this statement, and the block never upgrades procurement or budget roles. */
export function sourceBoundBomSubtotalPromptBlock(args: BomSubtotalPromptSource): string {
  const authority = args.sourceAuthority;
  if (typeof authority?.ownerUid !== 'string' || !authority.ownerUid.trim()
    || !args.siteId || authority.siteId !== args.siteId
    || authority.sedId !== args.sed.id || authority.sedVersion !== args.sed.version) return '';
  const value = args.sed.supplyChainSnapshot?.data?.bomCapexTotalUsd;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return '';
  const amount = value.toLocaleString('en-US', { maximumFractionDigits: 20 });
  // Never turn an unrenderable fractional amount into a rounded source claim.
  if (Number(amount.replace(/,/g, '')) !== value) return '';
  const raw = `$${amount}`;
  const source: FrozenBomSubtotalSource = {
    siteId: args.siteId, sed: args.sed,
    report: { siteId: args.siteId, sedId: authority.sedId,
      sedVersionUsed: authority.sedVersion, references: args.references },
  };
  for (let refIndex = 0; refIndex < args.references.length; refIndex++) {
    if (args.references[refIndex]?.field !== 'supplyChainSnapshot') continue;
    const statement = `The recorded BOM subtotal is ${raw} [Ref ${refIndex + 1}].`;
    const checked = checkSourceBoundBomSubtotal({ text: statement, index: statement.indexOf(raw),
      raw, value, unit: 'USD', siteId: args.siteId, source });
    if (!checked?.supported) continue;
    return `\n═══ FROZEN WHOLE-BOM SUBTOTAL (SEPARATE COST ROLE) ═══\n${statement}\n` +
      'When reporting this subtotal, reproduce the complete sentence with its own frozen supply-chain snapshot citation. Keep it in a separate sentence from any modeled whole-project budget or contingency. ' +
      'This screening subtotal does not establish a phase budget, all-in project capital, an EPC or vendor quote, a contract or award price, payment, or installed equipment. Preserve each item\'s separate planned, quoted, ordered and delivered status.\n';
  }
  return '';
}

/** Only these report producers consume this additional cost-role context. */
export function sourceBoundBomSubtotalPromptBlockForReport(
  reportType: string | undefined, source: BomSubtotalPromptSource,
): string {
  return reportType === 'test-fit' || reportType === 'supply-chain-risk'
    ? sourceBoundBomSubtotalPromptBlock(source) : '';
}
